/**
 * Vorlage „Hacken“.
 *
 * Draufsicht: Die Messerspitze bleibt auf dem Brett, der Griff wippt schnell
 * auf und ab, und das Messer wandert fächerförmig über den Haufen. Aus
 * Blättern, Zehen oder Nüssen werden feine Stückchen.
 */
import { ease, lerp, random, segment, wave } from '../geometry.ts';
import type { Point } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import type { Tone } from '../palette.ts';
import { path } from '../paths.ts';
import { compact, faded, group, rect, rotated, shape } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { KNIFE_BLADE, board, knife } from '../parts/knife.ts';
import { arcArrow } from '../parts/stage.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

/** Mitte des Haufens. */
const PILE = { x: 104, y: 74, rx: 46, ry: 26 } as const;
/** Drehpunkt an der Messerspitze, links vom Haufen. */
const TIP: Point = { x: 54, y: 74 };
const KNIFE_SCALE = 0.95;
const BLADE = KNIFE_BLADE * KNIFE_SCALE;

/** Zeitplan. */
const TIME = { chop: [0.06, 0.84], reset: [0.9, 1] } as const;
/** Zahl der Hackbewegungen je Schleife. */
const CHOPS = 14;

/** Zutaten zum Hacken: Ton und Form der ganzen Stücke. */
const ITEMS = { herbs: 'Kräuter', garlic: 'Knoblauch', nuts: 'Nüsse' } as const;
type Item = keyof typeof ITEMS;
const BIT_STYLES: Readonly<
  Record<Item, { size: number; style: { fill: Tone; stroke: Tone; strokeWidth?: number } }>
> = {
  herbs: { size: 3.4, style: { fill: 'deep', stroke: 'none' } },
  garlic: { size: 4.2, style: { fill: 'snow', stroke: 'cavity', strokeWidth: STROKE.fine } },
  nuts: { size: 4.8, style: { fill: 'shade', stroke: 'ink', strokeWidth: STROKE.fine } },
};

/** Ein ganzes Stück: Blatt, Zehe oder Nusshälfte. */
function whole(item: Item, x: number, y: number, turn: number): SceneNode {
  if (item === 'herbs') {
    const leaf = path()
      .move(x - 13, y)
      .quad(x, y - 10, x + 13, y)
      .quad(x, y + 10, x - 13, y)
      .close()
      .toString();
    return rotated(turn, x, y, [
      shape(leaf, { fill: 'shade', stroke: 'ink', strokeWidth: STROKE.detail }),
      shape(
        path()
          .move(x - 10, y)
          .line(x + 10, y)
          .toString(),
        { stroke: 'deep', strokeWidth: STROKE.fine },
      ),
      shape(
        path()
          .move(x - 13, y)
          .line(x - 18, y + 1)
          .toString(),
        { stroke: 'ink', strokeWidth: STROKE.fine },
      ),
    ]);
  }
  if (item === 'garlic') {
    const clove = path()
      .move(x - 8, y + 3)
      .quad(x - 6, y - 6, x + 8, y - 2)
      .quad(x + 4, y + 6, x - 8, y + 3)
      .close()
      .toString();
    return rotated(turn, x, y, [
      shape(clove, { fill: 'snow', stroke: 'cavity', strokeWidth: STROKE.detail }),
    ]);
  }
  const nut = path()
    .move(x - 8, y)
    .quad(x - 7, y - 7, x, y - 6)
    .quad(x + 7, y - 7, x + 8, y)
    .quad(x + 6, y + 7, x, y + 5)
    .quad(x - 6, y + 7, x - 8, y)
    .close()
    .toString();
  return rotated(turn, x, y, [
    shape(nut, { fill: 'shade', stroke: 'ink', strokeWidth: STROKE.detail }),
    shape(
      path()
        .move(x, y - 5)
        .quad(x - 2, y, x, y + 4)
        .toString(),
      { stroke: 'deep', strokeWidth: STROKE.fine },
    ),
  ]);
}

/** Lage der ganzen Stücke und der Krümel, fest über den Startwert. */
function layout(
  count: number,
  seed: number,
  spreadX: number,
  spreadY: number,
): { x: number; y: number; turn: number }[] {
  const rng = random(seed);
  return Array.from({ length: count }, () => {
    const angle = rng() * Math.PI * 2;
    const distance = Math.sqrt(rng());
    return {
      x: PILE.x + Math.cos(angle) * spreadX * distance,
      y: PILE.y + Math.sin(angle) * spreadY * distance,
      turn: rng() * 180,
    };
  });
}

const WHOLE = layout(10, 4, PILE.rx * 0.7, PILE.ry * 0.7);
const BITS = layout(80, 8, PILE.rx, PILE.ry);

/** Haufen bei Fortschritt amount (0 ganz, 1 fein gehackt). */
function pile(item: Item, amount: number): SceneNode {
  // Krümel: Kräuter als dunkle Punkte ohne Kontur, Knoblauch und Nüsse größer mit feiner Kontur.
  const bitStyle = BIT_STYLES[item];
  const bits = BITS.slice(0, Math.round(BITS.length * amount)).map((bit) =>
    rotated(bit.turn, bit.x, bit.y, [
      rect(
        bit.x - bitStyle.size / 2,
        bit.y - bitStyle.size * 0.4,
        bitStyle.size,
        bitStyle.size * 0.8,
        0.8,
        bitStyle.style,
      ),
    ]),
  );
  const wholes = WHOLE.map((piece, index) => {
    const gone = segment(amount, (index / WHOLE.length) * 0.7, (index / WHOLE.length) * 0.7 + 0.3);
    return faded(1 - gone, [whole(item, piece.x, piece.y, piece.turn)]);
  });
  return group([...bits, ...wholes]);
}

/** Vorlage „Hacken“. */
export const cutChopTemplate = defineTemplate({
  id: 'cut.chop',
  group: 'cut',
  title: 'Hacken',
  description:
    'Die Messerspitze bleibt auf dem Brett, der Griff wippt auf und ab, und das Messer wandert über den Haufen, bis alles fein gehackt ist.',
  durationSeconds: 6,
  keywords: [
    'hacken',
    'fein hacken',
    'grob hacken',
    'gehackt',
    'kräuter',
    'petersilie',
    'schnittlauch',
    'zerkleinern',
  ],
  stillTime: 0.5,
  parameters: [choice('item', 'Zutat', ITEMS, 'herbs')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (/knoblauch/.test(lower)) return { item: 'garlic' };
    if (/nüsse|nuss|mandel|walnuss|haselnuss/.test(lower)) return { item: 'nuts' };
    if (/kräuter|petersilie|schnittlauch|basilikum|dill|koriander|minze/.test(lower))
      return { item: 'herbs' };
    return {};
  },
  scene(values, t) {
    const item = textValue(values, 'item', 'herbs') as Item;
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const chop = segment(t, ...TIME.chop);
    const active = chop > 0 && chop < 1;

    // Wippen: Spitze bleibt liegen, der Griff hebt und senkt sich. Dazu
    // wandert das Messer langsam über den Haufen hin und zurück.
    const rock = active ? (1 - Math.cos(chop * CHOPS * Math.PI * 2)) / 2 : 0.6;
    const fan = active ? wave(chop, 1.5) * 22 : -6;
    const radians = (fan * Math.PI) / 180;
    const pose = {
      x: TIP.x + Math.cos(radians) * BLADE,
      y: TIP.y + Math.sin(radians) * BLADE,
      rotate: fan,
      lift: rock,
    };
    return compact([
      ...board(),
      reset > 0 ? group([pile(item, 0)]) : null,
      faded(1 - reset, [pile(item, ease.inOutSine(chop))]),
      knife({ ...pose, scale: KNIFE_SCALE, squash: lerp(0.55, 0.75, rock) }),
    ]);
  },
  hints() {
    return [arcArrow(TIP.x, TIP.y, BLADE * 0.9, BLADE * 0.9, -26, 26)];
  },
});
